import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  Archive,
  ArchiveRestore,
  BookOpen,
  Calendar,
  CheckCircle2,
  Circle,
  CircleDot,
  Clock,
  FileText,
  History,
  Info,
  Link2Off,
  ListTodo,
  Lock,
  MessageSquare,
  Pencil,
  Plus,
  Trash2,
  Upload,
  Users,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { api, type Project, type Task } from '../../lib/api'
import { budgetClasses, hasBudgetData } from '../../lib/budget-view'
import { notify } from '../../lib/notify'
import { cn, formatDuration } from '../../lib/utils'
import { BudgetHint, BudgetPeriodLabel, BudgetRates, BudgetSummary } from '../budget/budget-summary'
import { NewChatDialog } from '../chat/new-chat-dialog'
import { MobileBackTitle } from '../shell/mobile-back-title'
import { MobileTopCenter } from '../shell/mobile-shell-outlets'
import { Button } from '../ui/button'
import { confirm } from '../ui/confirm-dialog'
import { Input } from '../ui/input'
import { BudgetPeriodsDialog } from './budget-periods-dialog'
import { PageEditor } from './page-editor'

type HubTab = 'overview' | 'pages' | 'tasks' | 'files' | 'chat' | 'times'

const TABS: { key: HubTab; label: string; icon: typeof Info }[] = [
  { key: 'overview', label: 'Übersicht', icon: Info },
  { key: 'pages', label: 'Seiten', icon: BookOpen },
  { key: 'tasks', label: 'Aufgaben', icon: ListTodo },
  { key: 'files', label: 'Dateien', icon: FileText },
  { key: 'chat', label: 'Chat', icon: MessageSquare },
  { key: 'times', label: 'Zeiten', icon: Clock },
]

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE')
}

const ACTIVITY_LABEL: Record<string, string> = {
  create: 'Projekt angelegt',
  update: 'Stammdaten geändert',
  archive: 'Projekt archiviert',
  unarchive: 'Projekt reaktiviert',
  update_access: 'Freigabe geändert',
  upload_documents: 'Dateien hochgeladen',
  page_create: 'Seite angelegt',
  page_update: 'Seite bearbeitet',
  page_delete: 'Seite gelöscht',
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatProjectRange(start: string | null, end: string | null): string {
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`
  if (start) return `ab ${formatDate(start)}`
  if (end) return `bis ${formatDate(end)}`
  return '—'
}

/**
 * Detail-Fläche des Projekt-Hubs: Kopf mit Stammdaten + Aktionen, darunter
 * Tabs (Übersicht / Dateien / Zeiten). Holt sein Projekt selbst über
 * ['project', id] — damit lösen Deep-Links auch auf archivierte oder nur
 * per Freigabe sichtbare Projekte auf, die in der gefilterten Liste fehlen.
 */
export function ProjectHub({
  projectId,
  myId,
  isAdmin,
  isPhone,
  customerName,
  onBack,
  onEdit,
  onManageAccess,
}: {
  projectId: string
  myId: string
  isAdmin: boolean
  isPhone: boolean
  /** Fallback-Anzeige, solange ['project', id] noch lädt. */
  customerName?: string
  onBack: () => void
  onEdit: (project: Project) => void
  onManageAccess: (project: Project) => void
}) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [tab, setTab] = useState<HubTab>('overview')
  const [budgetPeriodsOpen, setBudgetPeriodsOpen] = useState(false)

  // Tab-Zustand pro Projekt zurücksetzen — sonst landet man beim
  // Projektwechsel auf einem leeren Dateien-Tab des vorherigen Projekts.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bewusster Reset bei Projektwechsel
  useEffect(() => {
    setTab('overview')
    setBudgetPeriodsOpen(false)
  }, [projectId])

  const {
    data: projectData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
    retry: (failureCount, err) =>
      // 404 (gelöscht/kein Zugriff) nicht wegretryen — sofort auf Liste zurück.
      !/not found/i.test((err as Error).message) && failureCount < 2,
  })

  const project = projectData?.project

  // Gelöschtes/unsichtbares Projekt: sauber auf die Liste zurückfallen —
  // aber NUR bei echtem 404 oder gescheitertem Erstladen. Ein Fehler eines
  // Background-Refetch (refetchOnWindowFocus nach API-Blip) lässt data
  // stehen und darf den offenen Hub nicht rauswerfen. bouncedRef verhindert
  // Mehrfach-Toast/-navigate, solange error über Renders hinweg ansteht.
  const bouncedRef = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Guard-Reset soll genau bei Projektwechsel feuern
  useEffect(() => {
    // Projektwechsel bei gemounteter Komponente: Guard zurücksetzen.
    bouncedRef.current = false
  }, [projectId])
  useEffect(() => {
    if (!error || bouncedRef.current) return
    const status = (error as { status?: number }).status
    if (status === 404) {
      bouncedRef.current = true
      notify.error('Projekt nicht gefunden oder kein Zugriff.')
      onBack()
    } else if (!project) {
      bouncedRef.current = true
      notify.error((error as Error).message)
      onBack()
    }
    // Background-Fehler MIT vorhandenen Daten: nichts tun, Daten weiter zeigen.
  }, [error, project, onBack])

  // Kunde über die Stammdaten auflösen (inkl. archivierter Kunden) — der
  // Listen-Fallback aus dem Parent greift nicht für Deep-Links auf Projekte
  // außerhalb der gerade gefilterten Liste.
  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: true }],
    queryFn: () => api.listCustomers(true),
    enabled: !!project,
  })
  const resolvedCustomerName = project
    ? (customersData?.customers.find((c) => c.id === project.customerId)?.name ??
      customerName ??
      '—')
    : (customerName ?? '—')

  // Zeit-Kennzahlen (gesamt) — Übersicht braucht sie für den Budget-Balken,
  // der Zeiten-Tab zeigt sie im Detail. Eine Query für beides.
  const { data: rangeData } = useQuery({
    queryKey: ['reports', 'range', { projectId }],
    queryFn: () => api.rangeReport({ projectId }),
    enabled: !!project,
  })

  // Kontingent (#511): Verbrauch ist projektweit, Raten gehören zum Anfragenden.
  // Eigene Buchungen invalidieren den Key über invalidateEntryData; das
  // Intervall fängt Buchungen anderer Nutzer ein.
  const budgetStatus = useQuery({
    queryKey: ['project-budget-status', projectId, { userId: myId }],
    queryFn: () => api.getBudgetStatus(projectId),
    enabled: !!project,
    retry: false,
    refetchInterval: 30_000,
  })

  // Dateien lazy: erst laden, wenn der Tab offen ist — Projektwechsel in
  // der Liste soll nicht pro Klick drei Queries feuern.
  const { data: docsData, isLoading: docsLoading } = useQuery({
    queryKey: ['project-documents', projectId],
    queryFn: () => api.listProjectDocuments(projectId),
    enabled: !!project && tab === 'files',
  })

  // Aufgaben des Projekts (Paket 2) — Key beginnt mit 'tasks', damit die
  // flachen invalidate(['tasks'])-Aufrufe von /tasks auch den Hub treffen.
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks', { projectId }],
    queryFn: () => api.listTasks({ projectId }),
    enabled: !!project && tab === 'tasks',
  })

  // Projekt-Chats (Paket 4): geteilter ['chats']-Key mit der Chat-Seite —
  // die Liste enthält NUR eigene Mitgliedschaften, gefiltert aufs Projekt.
  const { data: chatsData, isLoading: chatsLoading } = useQuery({
    queryKey: ['chats'],
    queryFn: () => api.listChats(),
    enabled: !!project && tab === 'chat',
  })
  const [newChatOpen, setNewChatOpen] = useState(false)

  // Projekt-Seiten (Paket 6): Liste lazy; die offene Seite lädt der Editor.
  const { data: pagesData, isLoading: pagesLoading } = useQuery({
    queryKey: ['project-pages', projectId],
    queryFn: () => api.listProjectPages(projectId),
    enabled: !!project && tab === 'pages',
  })
  const [activePageId, setActivePageId] = useState<string | null>(null)
  const [newPageTitle, setNewPageTitle] = useState('')
  // biome-ignore lint/correctness/useExhaustiveDependencies: Seitenauswahl beim Projektwechsel zurücksetzen
  useEffect(() => setActivePageId(null), [projectId])
  const createPage = useMutation({
    mutationFn: (title: string) => api.createProjectPage(projectId, title),
    onSuccess: (r) => {
      setNewPageTitle('')
      qc.invalidateQueries({ queryKey: ['project-pages', projectId] })
      setActivePageId(r.page.id)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Aktivitäts-Feed (Paket 5): pollt wie der Chat alle 15s, aber nur
  // solange die Übersicht offen ist.
  const { data: activityData } = useQuery({
    queryKey: ['project-activity', projectId],
    queryFn: () => api.listProjectActivity(projectId),
    enabled: !!project && tab === 'overview',
    refetchInterval: 15_000,
  })

  // Inline-Notizen (Paket 5): Beschreibung direkt in der Übersicht pflegen.
  const [notesEdit, setNotesEdit] = useState<string | null>(null)
  const saveNotes = useMutation({
    mutationFn: (description: string) =>
      api.updateProject(projectId, { description: description.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['project', projectId] })
      qc.invalidateQueries({ queryKey: ['project-activity', projectId] })
      setNotesEdit(null)
      notify.success('Notizen gespeichert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Monatsbeginn in LOKALER Zeit — toISOString() ist UTC und kippt am
  // Monatsersten vor 01:00 (CET) auf den Vormonat.
  const now = new Date()
  const monthFrom = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const { data: monthData } = useQuery({
    queryKey: ['reports', 'range', { projectId, month: monthFrom }],
    queryFn: () => api.rangeReport({ projectId, from: monthFrom }),
    enabled: !!project && tab === 'times',
  })

  const archive = useMutation({
    mutationFn: () => {
      if (!project) throw new Error('Projekt nicht geladen')
      return api.updateProject(project.id, {
        archived: !project.archived,
        baseVersion: project.version,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['project', projectId] })
      notify.success(project?.archived ? 'Projekt reaktiviert' : 'Projekt archiviert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const remove = useMutation({
    mutationFn: () => api.deleteProject(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      // Cache-Eintrag hart entfernen — sonst rendert Browser-Back auf
      // ?id=<gelöscht> den Geist 30s lang aus dem 'frischen' Cache.
      qc.removeQueries({ queryKey: ['project', projectId] })
      notify.success('Projekt gelöscht')
      onBack()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Aufgaben: Toggle + Quick-Add — flache ['tasks']-Invalidierung deckt
  // /tasks UND alle Hub-Tabs ab.
  const [quickTask, setQuickTask] = useState('')
  const invalidateTasks = () => qc.invalidateQueries({ queryKey: ['tasks'] })
  const toggleTask = useMutation({
    mutationFn: (t: Task) =>
      api.updateTask(t.id, { status: t.status === 'done' ? 'open' : 'done' }),
    onSuccess: invalidateTasks,
    onError: (err: Error) => notify.error(err.message),
  })
  const addTask = useMutation({
    mutationFn: (title: string) => api.createTask({ title, projectId }),
    onSuccess: () => {
      setQuickTask('')
      invalidateTasks()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Datei-Upload direkt ins Projekt (Paket 3).
  const fileInput = useRef<HTMLInputElement>(null)
  const invalidateDocs = () => {
    qc.invalidateQueries({ queryKey: ['project-documents', projectId] })
    qc.invalidateQueries({ queryKey: ['documents'] })
  }
  const uploadFiles = useMutation({
    mutationFn: (files: File[]) => api.uploadProjectDocuments(projectId, files),
    onSuccess: (r) => {
      invalidateDocs()
      notify.success(`${r.documents.length} Datei(en) hochgeladen`)
    },
    onError: (err: Error) => notify.error(err.message),
  })
  const unlinkDoc = useMutation({
    mutationFn: (docId: string) => api.updateDocument(docId, { projectId: null }),
    onSuccess: () => {
      invalidateDocs()
      notify.success('Verknüpfung gelöst')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (isLoading || !project) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-text-tertiary">
        {/* Auch während des Ladens braucht das Phone einen ←-Rückweg —
            sonst gibt es bei hängender API keinen In-App-Weg zur Liste. */}
        {isPhone && (
          <MobileTopCenter>
            <MobileBackTitle
              title="Projekt"
              onBack={onBack}
              backAriaLabel="Zurück zur Projektliste"
            />
          </MobileTopCenter>
        )}
        Lade Projekt …
      </div>
    )
  }

  const totalMs = rangeData?.totalMs ?? 0
  const budgetHours = project.budgetHours ? Number(project.budgetHours) : null
  const trackedHours = totalMs / 3_600_000
  const budgetPct = budgetHours ? Math.min(100, (trackedHours / budgetHours) * 100) : null
  const legacyBudgetClasses = budgetClasses(budgetPct)
  const projectRow = rangeData?.byProject.find((p) => p.projectId === projectId)
  const revenueCents = projectRow?.revenueCents ?? null
  const canEdit = project._canEdit === true
  const documents = docsData?.documents ?? []
  const projectTasks = tasksData?.tasks ?? []
  const openTasks = projectTasks.filter((t) => t.status !== 'done')
  const projectChats = (chatsData?.chats ?? []).filter((c) => c.projectId === projectId)

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Phone: ←+Projektname oben in der Shell; ← räumt den ?id-Param. */}
      {isPhone && (
        <MobileTopCenter>
          <MobileBackTitle
            title={project.name}
            onBack={onBack}
            backAriaLabel="Zurück zur Projektliste"
          />
        </MobileTopCenter>
      )}

      {/* Kopf: Stammdaten + Aktionen (gleiche Gates wie die alte Tabelle). */}
      <header className="px-4 sm:px-5 pt-4 pb-0 border-b border-border-subtle">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base sm:text-lg font-medium text-text-primary">
              {project.color && (
                <span
                  aria-hidden
                  className="inline-block h-3 w-3 rounded-full shrink-0"
                  style={{ backgroundColor: project.color }}
                />
              )}
              {project.restricted && (
                <span title="Beschränkter Zugriff" className="shrink-0">
                  <Lock className="h-3.5 w-3.5 text-accent" aria-label="Beschränkter Zugriff" />
                </span>
              )}
              <span className="truncate" title={project.name}>
                {project.name}
              </span>
            </h2>
            <p className="mt-0.5 text-xs text-text-tertiary truncate">
              {resolvedCustomerName}
              {project.archived && (
                <span className="ml-2 inline-flex items-center rounded-full bg-bg-overlay px-2 py-0.5 text-[11px] text-text-secondary">
                  Archiviert
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canEdit && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onEdit(project)}
                aria-label="Bearbeiten"
                title="Bearbeiten"
                className="text-text-tertiary hover:text-accent"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {isAdmin && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onManageAccess(project)}
                aria-label="Freigabe verwalten"
                title="Freigabe verwalten"
                className={
                  project.restricted
                    ? 'text-accent hover:text-accent'
                    : 'text-text-tertiary hover:text-accent'
                }
              >
                <Lock className="h-4 w-4" />
              </Button>
            )}
            {canEdit && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => archive.mutate()}
                disabled={archive.isPending}
                aria-label={project.archived ? 'Reaktivieren' : 'Archivieren'}
                title={project.archived ? 'Reaktivieren' : 'Archivieren'}
                className="text-text-tertiary hover:text-accent"
              >
                {project.archived ? (
                  <ArchiveRestore className="h-4 w-4" />
                ) : (
                  <Archive className="h-4 w-4" />
                )}
              </Button>
            )}
            {isAdmin && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={async () => {
                  const ok = await confirm({
                    title: `„${project.name}“ löschen?`,
                    description: 'Time-Entries verlieren ihren Project-Bezug.',
                    variant: 'danger',
                    confirmLabel: 'Löschen',
                  })
                  if (ok) remove.mutate()
                }}
                aria-label="Löschen"
                title="Löschen"
                className="text-text-tertiary hover:text-danger"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Tab-Leiste — horizontal scrollbar, damit auf schmalen Phones kein
            Tab geklippt/unbedienbar wird. */}
        <nav className="mt-3 flex gap-1 -mb-px overflow-x-auto" aria-label="Projekt-Bereiche">
          {TABS.map(({ key, label, icon: TabIcon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              aria-current={tab === key ? 'page' : undefined}
              className={`inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors ${
                tab === key
                  ? 'border-accent text-text-primary'
                  : 'border-transparent text-text-tertiary hover:text-text-secondary'
              }`}
            >
              <TabIcon className="h-3.5 w-3.5" />
              {label}
              {key === 'files' && docsData && (
                <span className="text-[11px] text-text-tertiary tabular-nums">
                  {documents.length}
                </span>
              )}
              {key === 'tasks' && tasksData && (
                <span className="text-[11px] text-text-tertiary tabular-nums">
                  {openTasks.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </header>

      {budgetPeriodsOpen && canEdit && (
        <BudgetPeriodsDialog
          key={projectId}
          projectId={projectId}
          projectName={project.name}
          onClose={() => setBudgetPeriodsOpen(false)}
        />
      )}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 py-4">
        {tab === 'overview' && (
          <div className="space-y-4 max-w-3xl">
            {/* 2 Spalten bis xl — sm: wäre Viewport-gebunden, das Pane ist im
                md-Band aber nur ~400px breit (Liste daneben). */}
            <dl className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5">
                <dt className="text-[11px] uppercase tracking-wider text-text-tertiary">
                  Zeitraum
                </dt>
                <dd className="mt-1 text-sm text-text-primary tabular-nums flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-text-tertiary shrink-0" />
                  {formatProjectRange(project.startDate, project.endDate)}
                </dd>
              </div>
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5">
                <dt className="text-[11px] uppercase tracking-wider text-text-tertiary">
                  Stundensatz
                </dt>
                <dd className="mt-1 text-sm font-mono text-accent tabular-nums">
                  {project.hourlyRateCents != null
                    ? `${(project.hourlyRateCents / 100).toFixed(2)} €/h`
                    : '—'}
                </dd>
              </div>
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5">
                <dt className="text-[11px] uppercase tracking-wider text-text-tertiary">
                  Erfasste Zeit
                </dt>
                <dd className="mt-1 text-sm font-mono text-text-primary tabular-nums">
                  {formatDuration(totalMs)}
                </dd>
              </div>
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5">
                <dt className="text-[11px] uppercase tracking-wider text-text-tertiary">Umsatz</dt>
                <dd className="mt-1 text-sm font-mono text-text-primary tabular-nums">
                  {revenueCents != null ? `${(revenueCents / 100).toFixed(2)} €` : '—'}
                </dd>
              </div>
            </dl>

            {budgetHours != null && (
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-text-tertiary">
                    Budget: {formatDuration(totalMs)} / {budgetHours}h
                  </span>
                  <span className={legacyBudgetClasses.text}>{budgetPct?.toFixed(0)}%</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-bg-overlay overflow-hidden">
                  <div
                    className={`h-full transition-all ${legacyBudgetClasses.bar}`}
                    style={{ width: `${budgetPct}%` }}
                  />
                </div>
              </div>
            )}

            <section
              aria-label="Kontingent"
              className="rounded-xl border border-border-subtle p-3 space-y-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-accent" />
                  Kontingent
                </h3>
                {hasBudgetData(budgetStatus.data) && (
                  <span className="text-xs text-text-secondary">
                    <BudgetPeriodLabel status={budgetStatus.data} />
                  </span>
                )}
              </div>
              {budgetStatus.isLoading ? (
                <p className="text-sm text-text-tertiary">Lade Kontingent …</p>
              ) : budgetStatus.error ? (
                <p className="text-sm text-text-tertiary">Kontingent nicht verfügbar.</p>
              ) : hasBudgetData(budgetStatus.data) ? (
                <>
                  <BudgetSummary status={budgetStatus.data} />
                  <BudgetRates status={budgetStatus.data} />
                  <BudgetHint />
                </>
              ) : (
                <p className="text-sm text-text-tertiary">Kein Kontingent für heute</p>
              )}
              {canEdit && (
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setBudgetPeriodsOpen(true)}>
                    Zeiträume pflegen
                  </Button>
                </div>
              )}
            </section>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-xs uppercase tracking-wider text-text-tertiary">Notizen</h3>
                {canEdit && notesEdit === null && (
                  <button
                    type="button"
                    className="text-xs text-accent hover:underline"
                    onClick={() => setNotesEdit(project.description ?? '')}
                  >
                    Bearbeiten
                  </button>
                )}
              </div>
              {notesEdit !== null ? (
                <div className="space-y-2">
                  <textarea
                    value={notesEdit}
                    onChange={(e) => setNotesEdit(e.target.value.slice(0, 4000))}
                    rows={6}
                    maxLength={4000}
                    placeholder="Ziele, Stakeholder, Absprachen — alles was das Projekt braucht …"
                    className="flex w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3.5 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-y min-h-[120px]"
                    // biome-ignore lint/a11y/noAutofocus: bewusster Fokus beim Öffnen des Editors
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setNotesEdit(null)}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={saveNotes.isPending}
                      onClick={() => saveNotes.mutate(notesEdit)}
                    >
                      Speichern
                    </Button>
                  </div>
                </div>
              ) : project.description ? (
                <p className="text-sm text-text-secondary whitespace-pre-wrap break-words">
                  {project.description}
                </p>
              ) : (
                <p className="text-sm text-text-tertiary">
                  Keine Notizen.{' '}
                  {canEdit && (
                    <button
                      type="button"
                      className="text-accent hover:underline"
                      onClick={() => setNotesEdit('')}
                    >
                      Hinzufügen
                    </button>
                  )}
                </p>
              )}
            </div>

            {/* Aktivitäts-Feed (Paket 5) */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-text-tertiary mb-1.5">
                Aktivität
              </h3>
              {!activityData ? (
                <p className="text-sm text-text-tertiary">Lade …</p>
              ) : activityData.entries.length === 0 ? (
                <p className="text-sm text-text-tertiary">Noch keine Aktivität.</p>
              ) : (
                <ul className="rounded-xl border border-border-subtle divide-y divide-border-subtle overflow-hidden">
                  {activityData.entries.map((e) => (
                    <li key={e.id} className="flex items-center gap-3 px-3.5 py-2">
                      <History className="h-3.5 w-3.5 text-text-tertiary shrink-0" />
                      <span className="flex-1 min-w-0 text-sm text-text-secondary truncate">
                        {ACTIVITY_LABEL[e.action] ?? e.action}
                        {e.meta?.files && (
                          <span className="text-text-tertiary"> — {e.meta.files.join(', ')}</span>
                        )}
                        {e.meta?.name && (
                          <span className="text-text-tertiary"> — {e.meta.name}</span>
                        )}
                        {e.meta?.title && (
                          <span className="text-text-tertiary"> — „{e.meta.title}“</span>
                        )}
                        {e.userName && <span className="text-text-tertiary"> · {e.userName}</span>}
                      </span>
                      <span className="text-[11px] text-text-tertiary tabular-nums shrink-0">
                        {formatDateTime(e.createdAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === 'pages' && (
          <div className="space-y-3 max-w-3xl">
            {activePageId ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setActivePageId(null)}
                  className="text-xs text-accent hover:underline"
                >
                  ← Alle Seiten
                </button>
                <PageEditor
                  projectId={projectId}
                  pageId={activePageId}
                  canEdit={canEdit}
                  onDeleted={() => setActivePageId(null)}
                />
              </div>
            ) : (
              <>
                {canEdit && (
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(e) => {
                      e.preventDefault()
                      const t = newPageTitle.trim()
                      if (t) createPage.mutate(t)
                    }}
                  >
                    <Input
                      value={newPageTitle}
                      onChange={(e) => setNewPageTitle(e.target.value.slice(0, 160))}
                      placeholder="Neue Seite — Titel eingeben …"
                      className="flex-1"
                    />
                    <Button type="submit" disabled={createPage.isPending || !newPageTitle.trim()}>
                      <Plus className="h-4 w-4" /> Anlegen
                    </Button>
                  </form>
                )}
                {pagesLoading ? (
                  <p className="py-8 text-center text-sm text-text-tertiary">Lade …</p>
                ) : (pagesData?.pages ?? []).length === 0 ? (
                  <div className="py-10 text-center text-sm text-text-tertiary space-y-2">
                    <BookOpen className="h-9 w-9 mx-auto text-text-tertiary/60" strokeWidth={1.5} />
                    <p>Noch keine Seiten in diesem Projekt.</p>
                  </div>
                ) : (
                  <ul className="rounded-xl border border-border-subtle divide-y divide-border-subtle overflow-hidden">
                    {(pagesData?.pages ?? []).map((pg) => (
                      <li key={pg.id}>
                        <button
                          type="button"
                          onClick={() => setActivePageId(pg.id)}
                          className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-bg-overlay/40 transition-colors"
                        >
                          <BookOpen className="h-4 w-4 text-text-tertiary shrink-0" />
                          <span className="flex-1 min-w-0 truncate text-sm text-text-primary">
                            {pg.title}
                          </span>
                          <span className="text-[11px] text-text-tertiary tabular-nums shrink-0">
                            {formatDateTime(pg.updatedAt)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}

        {tab === 'tasks' && (
          <div className="space-y-3 max-w-3xl">
            {/* Quick-Add: legt direkt in DIESEM Projekt an (Assignee = ich). */}
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                const t = quickTask.trim()
                if (t) addTask.mutate(t)
              }}
            >
              <Input
                value={quickTask}
                onChange={(e) => setQuickTask(e.target.value)}
                placeholder="Neue Aufgabe in diesem Projekt …"
                maxLength={200}
                className="flex-1"
              />
              <Button type="submit" disabled={addTask.isPending || !quickTask.trim()}>
                <Plus className="h-4 w-4" /> Anlegen
              </Button>
            </form>

            {tasksLoading ? (
              <p className="py-8 text-center text-sm text-text-tertiary">Lade …</p>
            ) : projectTasks.length === 0 ? (
              <div className="py-10 text-center text-sm text-text-tertiary space-y-2">
                <ListTodo className="h-9 w-9 mx-auto text-text-tertiary/60" strokeWidth={1.5} />
                <p>Noch keine Aufgaben in diesem Projekt.</p>
              </div>
            ) : (
              <ul className="rounded-xl border border-border-subtle divide-y divide-border-subtle overflow-hidden">
                {projectTasks.map((t) => {
                  const done = t.status === 'done'
                  const StatusIcon = done
                    ? CheckCircle2
                    : t.status === 'in_progress'
                      ? CircleDot
                      : Circle
                  return (
                    <li key={t.id} className="flex items-center gap-3 px-3.5 py-2.5">
                      <button
                        type="button"
                        onClick={() => toggleTask.mutate(t)}
                        aria-label={done ? 'Wieder öffnen' : 'Als erledigt markieren'}
                        className={cn(
                          'shrink-0 rounded-full p-1 touch:p-2',
                          done ? 'text-success' : 'text-text-tertiary hover:text-accent',
                        )}
                      >
                        <StatusIcon className="h-5 w-5" />
                      </button>
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            'truncate text-sm',
                            done ? 'text-text-tertiary line-through' : 'text-text-primary',
                          )}
                        >
                          {t.title}
                        </p>
                        <p className="flex flex-wrap items-center gap-x-2 text-[11px] text-text-tertiary">
                          {t.dueDate && <span className="font-mono tabular-nums">{t.dueDate}</span>}
                          {t.assigneeName && <span>{t.assigneeName}</span>}
                        </p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
            <p className="text-xs text-text-tertiary">
              Zeigt Aufgaben aus deinem Sichtbarkeits-Bereich (eigene + geführte Personen).
              Zuweisung, Priorität und Fälligkeit pflegst du unter »Aufgaben« — dort trägt jede
              Aufgabe ihr Projekt als Badge.
            </p>
          </div>
        )}

        {tab === 'files' && (
          <div className="space-y-3 max-w-3xl">
            {canEdit && (
              <div className="flex justify-end gap-2">
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? [])
                    if (files.length > 0) uploadFiles.mutate(files)
                    e.target.value = ''
                  }}
                />
                {/* Picker-Button nur Admin — der PUT-Endpoint dahinter ist
                    admin-only, für canEdit-User wäre er eine stumme Sackgasse. */}
                {isAdmin && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onEdit(project)}
                  >
                    <FileText className="h-3.5 w-3.5" /> Verknüpfungen bearbeiten
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  disabled={uploadFiles.isPending}
                  onClick={() => fileInput.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5" />
                  {uploadFiles.isPending ? 'Lade hoch …' : 'Hochladen'}
                </Button>
              </div>
            )}
            {docsLoading ? (
              <p className="py-8 text-center text-sm text-text-tertiary">Lade …</p>
            ) : documents.length === 0 ? (
              <div className="py-10 text-center text-sm text-text-tertiary space-y-2">
                <FileText className="h-9 w-9 mx-auto text-text-tertiary/60" strokeWidth={1.5} />
                <p>Noch keine Dateien mit diesem Projekt verknüpft.</p>
              </div>
            ) : (
              <ul className="rounded-xl border border-border-subtle divide-y divide-border-subtle overflow-hidden">
                {documents.map((doc) => (
                  <li
                    key={doc.id}
                    className="group flex items-center hover:bg-bg-overlay/40 transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
                      className="flex-1 min-w-0 flex items-center gap-3 px-3.5 py-2.5 text-left"
                    >
                      <FileText className="h-4 w-4 text-text-tertiary shrink-0" />
                      <span className="flex-1 min-w-0 truncate text-sm text-text-primary">
                        {doc.filename}
                      </span>
                      <span className="text-[11px] text-text-tertiary tabular-nums shrink-0">
                        {formatBytes(doc.sizeBytes)}
                      </span>
                      <span className="text-[11px] text-text-tertiary tabular-nums shrink-0">
                        {formatDate(doc.createdAt)}
                      </span>
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await confirm({
                            title: `„${doc.filename}“ vom Projekt lösen?`,
                            description:
                              'Die Datei bleibt im Datei-Vault erhalten, verschwindet aber aus diesem Projekt.',
                            confirmLabel: 'Lösen',
                          })
                          if (ok) unlinkDoc.mutate(doc.id)
                        }}
                        disabled={unlinkDoc.isPending}
                        aria-label={`${doc.filename} vom Projekt lösen`}
                        title="Vom Projekt lösen"
                        className="shrink-0 p-2 mr-1.5 rounded-md text-text-tertiary opacity-0 group-hover:opacity-100 touch:opacity-100 hover:text-danger transition-opacity"
                      >
                        <Link2Off className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === 'chat' && (
          <div className="space-y-3 max-w-3xl">
            <div className="flex justify-end">
              <Button type="button" size="sm" onClick={() => setNewChatOpen(true)}>
                <Plus className="h-3.5 w-3.5" /> Projekt-Chat anlegen
              </Button>
            </div>
            {chatsLoading ? (
              <p className="py-8 text-center text-sm text-text-tertiary">Lade …</p>
            ) : projectChats.length === 0 ? (
              <div className="py-10 text-center text-sm text-text-tertiary space-y-2">
                <MessageSquare
                  className="h-9 w-9 mx-auto text-text-tertiary/60"
                  strokeWidth={1.5}
                />
                <p>Du bist in keinem Chat dieses Projekts.</p>
              </div>
            ) : (
              <ul className="rounded-xl border border-border-subtle divide-y divide-border-subtle overflow-hidden">
                {projectChats.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => navigate({ to: '/chat', search: { id: c.id } })}
                      className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-bg-overlay/40 transition-colors"
                    >
                      <span className="h-8 w-8 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
                        <Users className="h-4 w-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm text-text-primary">
                          {c.name ?? 'Gruppe'}
                        </span>
                        <span className="block truncate text-[11px] text-text-tertiary">
                          {c.members.length} Mitglieder
                        </span>
                      </span>
                      {c.unread > 0 && (
                        <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-semibold leading-none text-bg-base tabular-nums shrink-0">
                          {c.unread > 99 ? '99+' : c.unread}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-text-tertiary">
              Hier siehst du nur Projekt-Chats, in denen du selbst Mitglied bist — Chats bleiben
              privat, Projekt-Zugriff öffnet keine fremden Konversationen.
            </p>
          </div>
        )}

        {tab === 'times' && (
          <div className="space-y-4 max-w-3xl">
            {/* 2 Spalten bis xl — sm: wäre Viewport-gebunden, das Pane ist im
                md-Band aber nur ~400px breit (Liste daneben). */}
            <dl className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5">
                <dt className="text-[11px] uppercase tracking-wider text-text-tertiary">Gesamt</dt>
                <dd className="mt-1 text-sm font-mono text-text-primary tabular-nums">
                  {formatDuration(totalMs)}
                </dd>
              </div>
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5">
                <dt className="text-[11px] uppercase tracking-wider text-text-tertiary">
                  Einträge
                </dt>
                <dd className="mt-1 text-sm font-mono text-text-primary tabular-nums">
                  {rangeData?.entries ?? 0}
                </dd>
              </div>
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5">
                <dt className="text-[11px] uppercase tracking-wider text-text-tertiary">
                  Dieser Monat
                </dt>
                <dd className="mt-1 text-sm font-mono text-text-primary tabular-nums">
                  {monthData ? formatDuration(monthData.totalMs) : '…'}
                </dd>
              </div>
              <div className="rounded-xl bg-bg-overlay/40 px-3 py-2.5">
                <dt className="text-[11px] uppercase tracking-wider text-text-tertiary">Umsatz</dt>
                <dd className="mt-1 text-sm font-mono text-text-primary tabular-nums">
                  {revenueCents != null ? `${(revenueCents / 100).toFixed(2)} €` : '—'}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-text-tertiary">
              Detaillierte Auswertungen mit Zeitraum-Filter findest du in den Berichten; der
              Kalender zeigt alle Einträge dieses Projekts farblich markiert.
            </p>
          </div>
        )}
      </div>

      {newChatOpen && (
        <NewChatDialog
          myId={myId}
          presetProjectId={projectId}
          presetName={project.name}
          onClose={() => setNewChatOpen(false)}
          onCreated={(chatId) => {
            setNewChatOpen(false)
            qc.invalidateQueries({ queryKey: ['chats'] })
            navigate({ to: '/chat', search: { id: chatId } })
          }}
        />
      )}
    </div>
  )
}
